import { useMemo } from 'react';
import { TourType } from '@/Components/Tour/Tour';
import { useLocalization } from '@/Hooks';
import { useDispatch, useSelector } from 'react-redux';
import { allocateSectionActions, allocateSectionSelectors } from '@/Pages/OnlineArchive/modules/Allocate/services';

export function useOnlineArchiveAllocate() {
  const dispatch = useDispatch();

  const {
    tours: {
      Accounting: { AllocateTour, AllocateModalTour },
    },
  } = useLocalization();

  const selectedDocumentInfo = useSelector(allocateSectionSelectors.selectedDocumentInfo);

  const pageTour = useMemo<TourType>(
    () => ({
      id: 'pageTour',
      title: AllocateTour.Title,
      steps: [
        {
          content: AllocateTour.Step1.Text,
          placement: 'auto',
          target: '#allocate',
          title: AllocateTour.Step1.Title,
          disableBeacon: true,
          link: AllocateTour.Step1.DetailsLink,
        },
        {
          content: AllocateTour.Step2.Text,
          placement: 'auto',
          target: '#allocateFolders',
          title: AllocateTour.Step2.Title,
          disableBeacon: true,
          link: AllocateTour.Step2.DetailsLink,
        },
        {
          content: AllocateTour.Step3.Text,
          placement: 'auto',
          target: '.deleteFolder',
          title: AllocateTour.Step3.Title,
          disableBeacon: true,
          link: AllocateTour.Step3.DetailsLink,
          styles: {
            spotlight: {
              maxHeight: '35px',
            },
          },
        },
        {
          content: AllocateTour.Step4.Text,
          placement: 'left',
          target: '#allocateSettings',
          title: AllocateTour.Step4.Title,
          disableBeacon: true,
          link: AllocateTour.Step4.DetailsLink,
        },
        {
          content: AllocateTour.Step5.Text,
          placement: 'left',
          target: '#documentsTable',
          title: AllocateTour.Step5.Title,
          disableBeacon: true,
          link: AllocateTour.Step5.DetailsLink,
          floaterProps: {
            disableFlip: true,
          },
          styles: {
            spotlight: {
              maxHeight: 'calc(100vh - 125px)',
            },
          },
        },
        {
          content: AllocateTour.Step6.Text,
          placement: 'auto',
          target: '.dx-command-select',
          title: AllocateTour.Step6.Title,
          disableBeacon: true,
          link: AllocateTour.Step6.DetailsLink,
        },
        {
          content: AllocateTour.Step7.Text,
          placement: 'auto',
          target: '#crudExtended',
          title: AllocateTour.Step7.Title,
          disableBeacon: true,
          link: AllocateTour.Step7.DetailsLink,
        },
        {
          content: AllocateTour.Step8.Text,
          placement: 'auto',
          target: '#openDocumentButton',
          title: AllocateTour.Step8.Title,
          disableBeacon: true,
          link: AllocateTour.Step8.DetailsLink,
        },
        {
          content: AllocateTour.Step9.Text,
          placement: 'auto',
          target: '.dx-toolbar-after',
          title: AllocateTour.Step9.Title,
          disableBeacon: true,
          link: AllocateTour.Step9.DetailsLink,
          spotlightPadding: 0,
        },
        {
          content: AllocateTour.Step10.Text,
          placement: 'auto',
          target: '#changeStatus',
          title: AllocateTour.Step10.Title,
          disableBeacon: true,
          link: AllocateTour.Step10.DetailsLink,
        },
      ],
    }),
    [AllocateTour],
  );

  const modalTour = useMemo<TourType>(
    () => ({
      id: 'modalTour',
      title: AllocateModalTour.Title,
      before: () => {
        dispatch(allocateSectionActions.setDocumentInfoModal({ visible: true, data: selectedDocumentInfo?.id }));
      },
      steps: [
        {
          content: AllocateModalTour.Step1.Text,
          placement: 'auto',
          target: '#modalTitle',
          title: AllocateModalTour.Step1.Title,
          disableBeacon: true,
          link: AllocateModalTour.Step1.DetailsLink,
        },
        {
          content: AllocateModalTour.Step2.Text,
          placement: 'auto',
          target: '#infoBlock',
          title: AllocateModalTour.Step2.Title,
          disableBeacon: true,
          link: AllocateModalTour.Step2.DetailsLink,
        },
        {
          content: AllocateModalTour.Step3.Text,
          placement: 'auto',
          target: '#copyValueButton',
          title: AllocateModalTour.Step3.Title,
          disableBeacon: true,
          link: AllocateModalTour.Step3.DetailsLink,
        },
        {
          content: AllocateModalTour.Step4.Text,
          placement: 'auto',
          target: '#customerDoc',
          title: AllocateModalTour.Step4.Title,
          disableBeacon: true,
          link: AllocateModalTour.Step4.DetailsLink,
        },
        {
          content: AllocateModalTour.Step5.Text,
          placement: 'top',
          target: '#accountingInfos',
          title: AllocateModalTour.Step5.Title,
          disableBeacon: true,
          link: AllocateModalTour.Step5.DetailsLink,
          styles: {
            spotlight: {
              maxHeight: '200px',
            },
          },
        },
        {
          content: AllocateModalTour.Step6.Text,
          placement: 'auto',
          target: '#addAccountingInfo',
          title: AllocateModalTour.Step6.Title,
          disableBeacon: true,
          link: AllocateModalTour.Step6.DetailsLink,
        },
        {
          content: AllocateModalTour.Step7.Text,
          placement: 'auto',
          target: '#totalAmount',
          title: AllocateModalTour.Step7.Title,
          disableBeacon: true,
          link: AllocateModalTour.Step7.DetailsLink,
        },
        {
          content: AllocateModalTour.Step8.Text,
          placement: 'auto',
          target: '#buttonsWrapper',
          title: AllocateModalTour.Step8.Title,
          disableBeacon: true,
          link: AllocateModalTour.Step8.DetailsLink,
        },
        {
          content: AllocateModalTour.Step9.Text,
          placement: 'left',
          target: '#previewSection',
          title: AllocateModalTour.Step9.Title,
          disableBeacon: true,
          link: AllocateModalTour.Step9.DetailsLink,
        },
        {
          content: AllocateModalTour.Step10.Text,
          placement: 'left',
          target: '#openDocumentPreview',
          title: AllocateModalTour.Step10.Title,
          disableBeacon: true,
          link: AllocateModalTour.Step10.DetailsLink,
        },
      ],
    }),
    [AllocateModalTour, selectedDocumentInfo],
  );

  return [pageTour, modalTour];
}
